import * as THREE from 'three';

export type LithophaneShape = 'flat' | 'curved' | 'cylinder';

export interface LithophaneParams {
  shape: LithophaneShape;
  width: number;             // Ancho en mm (ej. 100)
  height: number;            // Alto en mm (ej. 130)
  minThickness: number;      // Espesor en áreas claras (ej. 0.8 mm)
  maxThickness: number;      // Espesor en áreas oscuras (ej. 3.0 mm)
  frameWidth: number;        // Ancho del marco perimetral en mm (ej. 3.0)
  frameThickness: number;    // Espesor del marco en mm (ej. 4.0)
  curveAngle: number;        // Ángulo de arco para modo curvo en grados (ej. 120°)
  hasStandBase: boolean;     // Base soporte para apoyar sobre mesa
  standDepth: number;        // Profundidad de la base en mm (ej. 35)
  inverted: boolean;         // Invertir blanco/negro (negativo fotográfico)
}

/**
 * Muestrea una imagen HTMLImageElement o Canvas para generar un mapa de alturas
 */
export function processImageToHeightMap(
  img: HTMLImageElement,
  resX = 120,
  resY = 150,
  inverted = false
): Float32Array {
  const canvas = document.createElement('canvas');
  canvas.width = resX;
  canvas.height = resY;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new Float32Array(resX * resY);

  ctx.drawImage(img, 0, 0, resX, resY);
  const imgData = ctx.getImageData(0, 0, resX, resY).data;
  const heights = new Float32Array(resX * resY);

  for (let y = 0; y < resY; y++) {
    for (let x = 0; x < resX; x++) {
      const idx = (y * resX + x) * 4;
      const r = imgData[idx];
      const g = imgData[idx + 1];
      const b = imgData[idx + 2];
      // Brillo percibido estándar (Luma)
      const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      // En litofanía FDM estándar: Blanco = fino (pasa luz), Negro = grueso (bloquea luz)
      const val = inverted ? brightness : 1.0 - brightness;
      heights[y * resX + x] = Math.max(0, Math.min(1, val));
    }
  }

  return heights;
}

/**
 * Genera mapa de alturas de prueba (degradé y patrón) si no se subió imagen aún
 */
export function generateDefaultHeightMap(resX = 100, resY = 130): Float32Array {
  const heights = new Float32Array(resX * resY);
  for (let y = 0; y < resY; y++) {
    for (let x = 0; x < resX; x++) {
      const u = x / resX;
      const v = y / resY;
      // Retrato abstracto de prueba
      const distCenter = Math.sqrt(Math.pow(u - 0.5, 2) + Math.pow(v - 0.45, 2));
      const face = Math.sin(distCenter * Math.PI * 4) * 0.3 + 0.5;
      heights[y * resX + x] = Math.max(0, Math.min(1, face));
    }
  }
  return heights;
}

/**
 * Genera la geometría 3D de la Litofanía
 */
export function generateLithophaneGeometry(
  params: LithophaneParams,
  heightMap: Float32Array,
  resX = 120,
  resY = 150
): THREE.BufferGeometry {
  const {
    shape = 'curved',
    width = 100,
    height = 130,
    minThickness = 0.8,
    maxThickness = 3.0,
    frameWidth = 3.0,
    frameThickness = 4.0,
    curveAngle = 120,
    hasStandBase = true,
    standDepth = 35
  } = params;

  const positions: number[] = [];
  const indices: number[] = [];

  const curveRad = (curveAngle * Math.PI) / 180;
  const radius = shape === 'cylinder' ? width / (Math.PI * 2) : (width / curveRad);

  // Cara frontal (con relieve de la foto) y Cara trasera (plana/curva suave)
  // Generamos una malla volumétrica cerrada (Manifold)
  const rows = resY;
  const cols = resX;

  // 1. Vértices frontales (Front Face)
  for (let y = 0; y <= rows; y++) {
    const v = y / rows;
    const posY = (1.0 - v) * height; // Invertir para que arriba coincida con la foto

    for (let x = 0; x <= cols; x++) {
      const u = x / cols;
      const mapX = Math.min(cols - 1, Math.floor(u * cols));
      const mapY = Math.min(rows - 1, Math.floor(v * rows));
      const normHeight = heightMap[mapY * cols + mapX] || 0.5;

      // Si está en el borde del marco, usar frameThickness
      const isFrame = (u * width < frameWidth) || ((1 - u) * width < frameWidth) ||
                      (v * height < frameWidth) || ((1 - v) * height < frameWidth);

      const thickness = isFrame ? frameThickness : minThickness + normHeight * (maxThickness - minThickness);

      if (shape === 'flat') {
        const posX = (u - 0.5) * width;
        const posZ = thickness;
        positions.push(posX, posY, posZ);
      } else if (shape === 'curved') {
        const angle = (u - 0.5) * curveRad;
        const currentR = radius + thickness;
        const posX = Math.sin(angle) * currentR;
        const posZ = Math.cos(angle) * currentR - radius;
        positions.push(posX, posY, posZ);
      } else {
        // Cylinder
        const angle = u * Math.PI * 2;
        const currentR = radius + thickness;
        const posX = Math.sin(angle) * currentR;
        const posZ = Math.cos(angle) * currentR;
        positions.push(posX, posY, posZ);
      }
    }
  }

  // 2. Vértices traseros (Back Face - lisa)
  const frontCount = positions.length / 3;
  for (let y = 0; y <= rows; y++) {
    const v = y / rows;
    const posY = (1.0 - v) * height;

    for (let x = 0; x <= cols; x++) {
      const u = x / cols;

      if (shape === 'flat') {
        const posX = (u - 0.5) * width;
        positions.push(posX, posY, 0);
      } else if (shape === 'curved') {
        const angle = (u - 0.5) * curveRad;
        const posX = Math.sin(angle) * radius;
        const posZ = Math.cos(angle) * radius - radius;
        positions.push(posX, posY, posZ);
      } else {
        const angle = u * Math.PI * 2;
        const posX = Math.sin(angle) * radius;
        const posZ = Math.cos(angle) * radius;
        positions.push(posX, posY, posZ);
      }
    }
  }

  const rowStride = cols + 1;

  // Caras frontales
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const a = y * rowStride + x;
      const b = (y + 1) * rowStride + x;
      const c = (y + 1) * rowStride + (x + 1);
      const d = y * rowStride + (x + 1);

      indices.push(a, b, d);
      indices.push(b, c, d);
    }
  }

  // Caras traseras (normales invertidas)
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const a = frontCount + y * rowStride + x;
      const b = frontCount + (y + 1) * rowStride + x;
      const c = frontCount + (y + 1) * rowStride + (x + 1);
      const d = frontCount + y * rowStride + (x + 1);

      indices.push(a, d, b);
      indices.push(b, d, c);
    }
  }

  // Coser laterales perimetrales (Top, Bottom, Left, Right)
  // Lateral Izquierdo
  for (let y = 0; y < rows; y++) {
    const fA = y * rowStride;
    const fB = (y + 1) * rowStride;
    const bA = frontCount + y * rowStride;
    const bB = frontCount + (y + 1) * rowStride;
    indices.push(fA, bA, fB);
    indices.push(bA, bB, fB);
  }

  // Lateral Derecho
  for (let y = 0; y < rows; y++) {
    const fA = y * rowStride + cols;
    const fB = (y + 1) * rowStride + cols;
    const bA = frontCount + y * rowStride + cols;
    const bB = frontCount + (y + 1) * rowStride + cols;
    indices.push(fA, fB, bA);
    indices.push(fB, bB, bA);
  }

  // Borde Superior
  for (let x = 0; x < cols; x++) {
    const fA = x;
    const fB = x + 1;
    const bA = frontCount + x;
    const bB = frontCount + x + 1;
    indices.push(fA, fB, bA);
    indices.push(fB, bB, bA);
  }

  // Borde Inferior
  const fRowBottom = rows * rowStride;
  const bRowBottom = frontCount + rows * rowStride;
  for (let x = 0; x < cols; x++) {
    const fA = fRowBottom + x;
    const fB = fRowBottom + x + 1;
    const bA = bRowBottom + x;
    const bB = bRowBottom + x + 1;
    indices.push(fA, bA, fB);
    indices.push(bA, bB, fB);
  }

  let geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  // Si tiene base de apoyo tipo stand
  if (hasStandBase && shape !== 'cylinder') {
    const baseGeom = new THREE.BoxGeometry(width * 1.05, 4.0, standDepth);
    baseGeom.translate(0, 2.0, standDepth * 0.2);

    const mergedPositions: number[] = [];
    const nonIdxGeom = geometry.toNonIndexed();
    const pos1 = nonIdxGeom.getAttribute('position');
    for (let i = 0; i < pos1.count; i++) {
      mergedPositions.push(pos1.getX(i), pos1.getY(i), pos1.getZ(i));
    }

    const nonIdxBase = baseGeom.toNonIndexed();
    const pos2 = nonIdxBase.getAttribute('position');
    for (let i = 0; i < pos2.count; i++) {
      mergedPositions.push(pos2.getX(i), pos2.getY(i), pos2.getZ(i));
    }

    geometry.dispose();
    baseGeom.dispose();

    geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(mergedPositions, 3));
    geometry.computeVertexNormals();
  }

  return geometry;
}

/**
 * Exportador directo a formato binario STL para Slicers 3D
 */
export function exportLithophaneToSTL(geometry: THREE.BufferGeometry, filename = 'litofania-mila.stl') {
  let geom = geometry.clone();
  if (geom.index) {
    geom = geom.toNonIndexed();
  }

  const posAttr = geom.getAttribute('position');
  const trianglesCount = posAttr.count / 3;

  const bufferLength = 80 + 4 + trianglesCount * 50;
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const dataView = new DataView(arrayBuffer);

  const header = 'MilaOS 3D Generator - Parametric Lithophane STL';
  for (let i = 0; i < 80; i++) {
    dataView.setUint8(i, i < header.length ? header.charCodeAt(i) : 0);
  }

  dataView.setUint32(80, trianglesCount, true);

  let offset = 84;
  const pA = new THREE.Vector3();
  const pB = new THREE.Vector3();
  const pC = new THREE.Vector3();
  const normal = new THREE.Vector3();
  const cb = new THREE.Vector3();
  const ab = new THREE.Vector3();

  for (let i = 0; i < posAttr.count; i += 3) {
    pA.fromBufferAttribute(posAttr, i);
    pB.fromBufferAttribute(posAttr, i + 1);
    pC.fromBufferAttribute(posAttr, i + 2);

    cb.subVectors(pC, pB);
    ab.subVectors(pA, pB);
    cb.cross(ab).normalize();
    normal.copy(cb);

    dataView.setFloat32(offset, normal.x, true);
    dataView.setFloat32(offset + 4, normal.y, true);
    dataView.setFloat32(offset + 8, normal.z, true);
    offset += 12;

    dataView.setFloat32(offset, pA.x, true);
    dataView.setFloat32(offset + 4, pA.y, true);
    dataView.setFloat32(offset + 8, pA.z, true);
    offset += 12;

    dataView.setFloat32(offset, pB.x, true);
    dataView.setFloat32(offset + 4, pB.y, true);
    dataView.setFloat32(offset + 8, pB.z, true);
    offset += 12;

    dataView.setFloat32(offset, pC.x, true);
    dataView.setFloat32(offset + 4, pC.y, true);
    dataView.setFloat32(offset + 8, pC.z, true);
    offset += 12;

    dataView.setUint16(offset, 0, true);
    offset += 2;
  }

  const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename.endsWith('.stl') ? filename : `${filename}.stl`;
  link.click();
  URL.revokeObjectURL(link.href);
}
